import { Schema } from "effect";

const SortDirection = Schema.Literal("asc", "desc");
const SortNulls = Schema.Literal("first", "last");

export const CreatedAtSort = Schema.Struct({
  kind: Schema.Literal("createdAt"),
  direction: SortDirection,
  nulls: SortNulls,
});
export type CreatedAtSort = typeof CreatedAtSort.Type;

export const ClosedAtSort = Schema.Struct({
  kind: Schema.Literal("closedAt"),
  direction: SortDirection,
  nulls: SortNulls,
});
export type ClosedAtSort = typeof ClosedAtSort.Type;

export const LastResponseAtSort = Schema.Struct({
  kind: Schema.Literal("lastResponseAt"),
  direction: SortDirection,
  nulls: SortNulls,
});
export type LastResponseAtSort = typeof LastResponseAtSort.Type;

export const LastCustomerMessageAtSort = Schema.Struct({
  kind: Schema.Literal("lastCustomerMessageAt"),
  direction: SortDirection,
  nulls: SortNulls,
});
export type LastCustomerMessageAtSort = typeof LastCustomerMessageAtSort.Type;

export const LastMerchantMessageAtSort = Schema.Struct({
  kind: Schema.Literal("lastMerchantMessageAt"),
  direction: SortDirection,
  nulls: SortNulls,
});
export type LastMerchantMessageAtSort = typeof LastMerchantMessageAtSort.Type;

export const TicketNumberSort = Schema.Struct({
  kind: Schema.Literal("ticketNumber"),
  direction: SortDirection,
  nulls: SortNulls,
});
export type TicketNumberSort = typeof TicketNumberSort.Type;

export const PrioritySort = Schema.Struct({
  kind: Schema.Literal("priority"),
  direction: SortDirection,
  nulls: SortNulls,
});
export type PrioritySort = typeof PrioritySort.Type;

export const SupportTicketSort = Schema.Union(
  CreatedAtSort,
  ClosedAtSort,
  LastResponseAtSort,
  LastCustomerMessageAtSort,
  LastMerchantMessageAtSort,
  TicketNumberSort,
  PrioritySort,
);
export type SupportTicketSort = typeof SupportTicketSort.Type;

export const DEFAULT_SUPPORT_TICKET_SORTS: readonly SupportTicketSort[] = [
  { kind: "lastResponseAt", direction: "desc", nulls: "last" },
];

export function encodeSupportTicketSorts(
  sorts: readonly SupportTicketSort[],
): string {
  return JSON.stringify(sorts);
}

export function supportTicketSortsAreEqual(
  a: readonly SupportTicketSort[],
  b: readonly SupportTicketSort[],
): boolean {
  return encodeSupportTicketSorts(a) === encodeSupportTicketSorts(b);
}

export function isDefaultSupportTicketSorts(
  sorts: readonly SupportTicketSort[],
): boolean {
  return supportTicketSortsAreEqual(sorts, DEFAULT_SUPPORT_TICKET_SORTS);
}

export function decodeSupportTicketSorts(
  encoded: string | null | undefined,
  fallback: readonly SupportTicketSort[] = [],
): readonly SupportTicketSort[] {
  if (!encoded) {
    return fallback;
  }

  try {
    const parsed = JSON.parse(encoded);
    const result = Schema.decodeUnknownEither(Schema.Array(SupportTicketSort))(
      parsed,
    );
    return result._tag === "Right" ? result.right : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Validate an already-parsed sort value (e.g. the JSON `sort` column stored on
 * a saved view) into a typed sort array. Unlike {@link decodeSupportTicketSorts}
 * this takes a parsed value rather than a URL-encoded string. Returns `null`
 * when the value is missing or not a valid, non-empty sort array, so callers
 * can fall back to the default sort.
 */
export function coerceSupportTicketSorts(
  value: unknown,
): readonly SupportTicketSort[] | null {
  if (value == null) {
    return null;
  }
  const result = Schema.decodeUnknownEither(Schema.Array(SupportTicketSort))(
    value,
  );
  if (result._tag !== "Right" || result.right.length === 0) {
    return null;
  }
  return result.right;
}
